/**
 * App-tile loaders: one per widget, each reading through the service the app
 * already uses for that fact, on the browser's Supabase client (the caller's
 * RLS — never the service role, never the master-only sync_history RPCs).
 * Every loader THROWS on a failed read: the hook turns that into a failed tile,
 * never into an empty one that reads as "nothing to see".
 */
import { getSupabaseClient } from '@/config';
import { eventText } from '@/services/dashboard/dashboardEventText';
import { buildDashboardEvents, buildSyncPills } from '@/services/dashboard/dashboardEvents';
import type { PlatformSyncStatus } from '@/services/dashboard/dashboardEvents';
import { fetchSyncStatuses } from '@/services/dashboard/syncStatusService';
import { fetchEfficiency, fetchPlatformTotals } from '@/services/insights/insightService';
import { computeInsights } from '@/services/insights/budgetInsightEngine';
import { fetchMMMStatus } from '@/services/mediaIntelligenceService';
import { fetchOutcomePeriod } from '@/services/outcomeImport/outcomeImportService';
import { monthStartOf, todayDataDate } from '@/services/dataDate';
import { fetchWorkspaceTargets } from '@/services/targets/targetsService';
import { ACTIVE_JOB_STATUSES } from '@/services/syncJobShared';
import { readAllRows } from '@/services/supabasePaging';
import { logError, warn } from '@/services/logger';
import type { AppWidget } from './lookerAppTiles';
import { budgetPacingOf, type MonthSpendRow } from './budgetPacingData';
import {
  attentionOf, backfillEta, backfillOf, dataQualityOf, freshnessOf, outcomesOf,
  type AppLabelKey, type AppTileData, type BackfillJobRow, type BackfillSummary, type FreshChannel, type LabelFn, type QueueAhead,
} from './lookerAppData';

type TFn = (key: string, options?: Record<string, unknown>) => string;

export interface AppLoaderContext {
  workspaceId: string;
  period: { start: string; end: string } | null;
  t: TFn;
  now: Date;
  /** The page currency (R3): forced or picked, else the only one; null = several and none picked. */
  currency: string | null;
}
export type AppLoader = (ctx: AppLoaderContext) => Promise<AppTileData>;

/** /insights' own window (ruling 6), not the page's date filter. */
const ATTENTION_DAYS = 30;
const PAGE = 1000;

/** Literal keys only, so the locale-integrity guard can see every one. */
const LABELS: Record<AppLabelKey, (t: TFn, vars?: Record<string, unknown>) => string> = {
  channel: (t) => t('looker.app.table.channel'),
  window: (t) => t('looker.app.table.window'),
  days: (t) => t('looker.app.table.days'),
  leads: (t) => t('looker.app.table.leads'),
  bookings: (t) => t('looker.app.table.bookings'),
  sales: (t) => t('looker.app.table.sales'),
  revenue: (t) => t('looker.app.table.revenue'),
  spend: (t) => t('looker.app.table.spend'),
  costPerLead: (t) => t('looker.app.table.costPerLead'),
  mediaRoas: (t) => t('looker.app.table.mediaRoas'),
  item: (t) => t('looker.app.table.item'),
  where: (t) => t('looker.app.table.where'),
  last30Days: (t) => t('looker.app.table.last30Days'),
  finding: (t) => t('looker.app.table.finding'),
  action: (t) => t('looker.app.table.action'),
  severity: (t) => t('looker.app.table.severity'),
  blocking: (t) => t('looker.app.table.blocking'),
  degraded: (t) => t('looker.app.table.degraded'),
  allTime: (t) => t('looker.app.table.allTime'),
  asOfToday: (t) => t('looker.app.table.asOfToday'),
  selectedPeriod: (t) => t('looker.app.table.selectedPeriod'),
  value: (t) => t('looker.app.table.value'),
  status: (t) => t('looker.app.table.status'),
  pacingPct: (t) => t('looker.app.table.pacingPct'),
  plan: (t) => t('looker.app.table.plan'),
  projected: (t) => t('looker.app.table.projected'),
  dataThrough: (t) => t('looker.app.table.dataThrough'),
  currency: (t) => t('looker.app.table.currency'),
  month: (t) => t('looker.app.table.month'),
  pacingDays: (t) => t('looker.app.table.pacingDays'),
  note: (t) => t('looker.app.table.note'),
  channelSum: (t) => t('looker.app.table.channelSum'),
  pacingUnder: (t) => t('looker.app.budgetPacing.status.under'),
  pacingOnTrack: (t) => t('looker.app.budgetPacing.status.onTrack'),
  pacingOver: (t) => t('looker.app.budgetPacing.status.over'),
  pacingTooEarly: (t) => t('looker.app.budgetPacing.status.tooEarly'),
  pacingNoPlan: (t) => t('looker.app.budgetPacing.noPlan'),
  pacingNoPlanInCurrency: (t) => t('looker.app.budgetPacing.noPlanInCurrency'),
  pacingPickCurrency: (t) => t('looker.app.budgetPacing.pickCurrency'),
  pacingUnknownCurrency: (t) => t('looker.app.budgetPacing.unknownCurrency'),
  pacingDerived: (t) => t('looker.app.budgetPacing.derived'),
  pacingNoDataYet: (t) => t('looker.app.budgetPacing.noDataYet'),
  outcomesMixedCurrency: (t) => t('looker.app.outcomes.mixedCurrency'),
  outcomesUnknownCurrency: (t) => t('looker.app.outcomes.unknownCurrency'),
  unknownCurrency: (t) => t('looker.app.outcomes.unknownCurrencyLabel'),
  outcomesDays: (t, vars) => t('looker.app.outcomes.days', vars),
};

export const appLabel = (t: TFn): LabelFn => (key, vars) => LABELS[key](t, vars);

const readStatuses = async (workspaceId: string): Promise<PlatformSyncStatus[]> => {
  const res = await fetchSyncStatuses(workspaceId);
  if (!res.success || !res.data) throw new Error(res.error ?? 'fetchSyncStatuses failed');
  return res.data.statuses;
};

/**
 * Each ad channel's sync for the /looker "data as of" pill (not an app box: it
 * runs for every workspace view, whatever the template holds). Throws on a
 * failed read; [] = no ad channel connected.
 */
export const loadChannelFreshness = async (workspaceId: string): Promise<FreshChannel[]> =>
  freshnessOf(await readStatuses(workspaceId), todayDataDate());

/** Built exactly as src/pages/Insights.tsx builds computeInsights' input. */
const loadAttention: AppLoader = async (ctx) => {
  const [eff, totals, statuses] = await Promise.all([
    fetchEfficiency(ctx.workspaceId, ctx.t, ATTENTION_DAYS),
    fetchPlatformTotals(ctx.workspaceId, ctx.t, ATTENTION_DAYS),
    readStatuses(ctx.workspaceId),
  ]);
  if (!eff.ads.success) throw new Error(eff.ads.error);
  const today = todayDataDate();
  const report = computeInsights({
    rows: eff.ads.rows,
    // undefined, never []: the engine reads `adSetRows ?? rows`.
    adSetRows: eff.adSets.success ? eff.adSets.rows : undefined,
    todayISO: today,
    latestDataDate: eff.ads.latestDataDate,
    windowDays: ATTENTION_DAYS,
    platformTotals: totals.success ? totals.data : undefined,
  });
  const events = buildDashboardEvents(report, buildSyncPills(statuses, today));
  // The same test /insights applies before it says the set is incomplete.
  const truncated = eff.ads.truncated || (eff.adSets.success && eff.adSets.truncated);
  return attentionOf(events, (e) => eventText(e, ctx.t), appLabel(ctx.t), truncated);
};

const loadDataQuality: AppLoader = async (ctx) => {
  // Strict: a swallowed probe failure would otherwise read as "nothing to fix".
  const status = await fetchMMMStatus({ workspaceIds: [ctx.workspaceId] }, ctx.t, { strict: true });
  return dataQualityOf(status.dataQuality, appLabel(ctx.t));
};

const loadOutcomes: AppLoader = async (ctx) => {
  const res = await fetchOutcomePeriod(ctx.workspaceId, ctx.period, ctx.t);
  if (!res.success || !res.data) throw new Error(res.error ?? 'fetchOutcomePeriod failed');
  return outcomesOf(res.data, appLabel(ctx.t));
};

/**
 * Box 6: this month (Bangkok) whatever the page's period, against the
 * workspace's approved plans. Either read failing throws (the tile fails).
 */
const loadBudgetPacing: AppLoader = async (ctx) => {
  const today = todayDataDate();
  const month = monthStartOf(today);
  const [spendRes, targets] = await Promise.all([
    getSupabaseClient().rpc('get_month_spend', { p_workspace_id: ctx.workspaceId, p_month: month }),
    fetchWorkspaceTargets(ctx.workspaceId),
  ]);
  if (spendRes.error) throw new Error(`get_month_spend failed: ${spendRes.error.message}`);
  const raw = spendRes.data ?? [];
  // Null = not measurable, never 0 (m5): sum(cost) is NULL when every cost in a group
  // is NULL, and Number(null) is 0, so test the raw value before converting it.
  const measurable = (r: (typeof raw)[number]): boolean => r.spend !== null && Number.isFinite(Number(r.spend));
  const dropped = raw.filter((r) => !measurable(r));
  if (dropped.length > 0) {
    warn('LookerBudgetPacing', 'get_month_spend rows without a measurable spend were left out',
      { dropped: dropped.map((r) => ({ platform: String(r.platform), currency: r.currency ? String(r.currency).trim() : null })) });
  }
  const spend = raw.filter(measurable).map((r): MonthSpendRow => ({
    platform: String(r.platform),
    currency: r.currency ? String(r.currency).trim() || null : null,
    spend: Number(r.spend),
    dataThrough: r.data_through ? String(r.data_through) : null,
  }));
  return budgetPacingOf({ today, currency: ctx.currency, plans: targets.plans, spend, label: appLabel(ctx.t) });
};

/**
 * The oldest OPEN dated job's created_at: where the current backfill window
 * starts. null = nothing open, so there is no window and no history to read.
 */
const readOpenAnchor = async (workspaceId: string): Promise<string | null> => {
  const { data, error } = await getSupabaseClient()
    .from('sync_jobs')
    .select('created_at')
    .eq('workspace_id', workspaceId)
    .in('status', [...ACTIVE_JOB_STATUSES])
    .not('start_date', 'is', null)
    .order('created_at', { ascending: true })
    .order('id', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as { created_at: string } | null)?.created_at ?? null;
};

/**
 * The dated jobs created at or after `anchor`, paged: PostgREST stops at 1000
 * rows with HTTP 200 and no error. Ordered by (created_at, id) so pages never
 * overlap. Bounded by the open window, not every job the workspace ever had.
 */
const readDatedJobsFrom = async (workspaceId: string, anchor: string): Promise<BackfillJobRow[]> => {
  const read = async (offset: number, acc: BackfillJobRow[]): Promise<BackfillJobRow[]> => {
    const { data, error } = await getSupabaseClient()
      .from('sync_jobs')
      .select('platform, status, start_date, end_date, started_at, heartbeat_at, created_at, account_id, completed_at')
      .eq('workspace_id', workspaceId)
      .not('start_date', 'is', null)
      .gte('created_at', anchor)
      .order('created_at', { ascending: true })
      .order('id', { ascending: true })
      .range(offset, offset + PAGE - 1);
    if (error) throw new Error(error.message);
    const rows = [...acc, ...((data ?? []) as BackfillJobRow[])];
    return (data ?? []).length === PAGE ? read(offset + PAGE, rows) : rows;
  };
  return read(0, []);
};

/**
 * Account id → name for this workspace. Only `id, account_name` are selected:
 * the token columns are not granted, so `select('*')` would fail. Paged by id.
 */
const readAccountNames = async (workspaceId: string): Promise<Record<string, string>> => {
  const { rows, error } = await readAllRows<{ id: string; account_name: string | null }>(
    (from, to) => getSupabaseClient()
      .from('media_platform_accounts')
      .select('id, account_name')
      .eq('workspace_id', workspaceId)
      .order('id', { ascending: true })
      .range(from, to),
    'backfill account names',
  );
  if (error) throw error instanceof Error ? error : new Error((error as { message?: string }).message ?? 'account names read failed');
  return Object.fromEntries(rows.filter((r) => r.account_name !== null).map((r) => [r.id, r.account_name as string]));
};

const NO_BACKFILL: BackfillSummary = { platforms: [], eta: { minutes: null, lowerBound: true, queueAhead: null } };

const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;

/**
 * Other workspaces' jobs ahead of ours in the worker's global queue
 * (`sync_queue_ahead`: two counts, nothing else). null on ANY failure: the ETA
 * is then the lower bound it always was, and the pill must never fail over it.
 */
const readQueueAhead = async (workspaceId: string): Promise<QueueAhead | null> => {
  try {
    const { data, error } = await getSupabaseClient().rpc('sync_queue_ahead', { p_workspace_id: workspaceId });
    if (error) throw new Error(error.message);
    const row = Array.isArray(data) ? (data[0] as Partial<QueueAhead> | undefined) : undefined;
    if (!row || !isCount(row.ahead) || !isCount(row.running)) throw new Error('sync_queue_ahead returned no usable row');
    return { ahead: row.ahead, running: row.running };
  } catch (err) {
    logError('LookerBackfill', 'queue-ahead read failed, ETA stays a lower bound', err);
    return null;
  }
};

/**
 * sync_jobs RLS (migration 114) admits members and the platform master; anyone
 * else would read an empty table and the box would vanish as "no backfill
 * running". Refuse instead: the pill says it could not load. `user_id` is
 * load-bearing: the member-list policy shows a master every member's row.
 */
const assertCanRead = async (workspaceId: string): Promise<void> => {
  const supabase = getSupabaseClient();
  const { data: auth, error: authError } = await supabase.auth.getUser();
  if (authError || !auth.user) throw new Error(authError?.message ?? 'backfill: not signed in');
  const { data, error } = await supabase
    .from('workspace_members')
    .select('workspace_id')
    .eq('workspace_id', workspaceId)
    .eq('user_id', auth.user.id)
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (data) return;
  const { data: master, error: masterError } = await supabase.rpc('is_platform_master');
  if (masterError) throw new Error(masterError.message);
  if (master !== true) throw new Error('backfill: the caller is not a member of this workspace, so sync_jobs would read as empty');
};

/**
 * The open history download for the /looker filter-bar pill (not an app box:
 * it runs for every workspace view, whatever the template holds): per platform
 * and account, plus an ETA (counting other workspaces' queued jobs when the
 * queue could be read, else a lower bound). Throws on a failed read or a caller who
 * is neither member nor platform master; no platforms = nothing open.
 */
export const loadBackfill = async (workspaceId: string, now: Date): Promise<BackfillSummary> => {
  await assertCanRead(workspaceId);
  const anchor = await readOpenAnchor(workspaceId);
  if (anchor === null) return NO_BACKFILL;
  const [jobs, names, queue] = await Promise.all([
    readDatedJobsFrom(workspaceId, anchor), readAccountNames(workspaceId), readQueueAhead(workspaceId),
  ]);
  // Both re-apply openWindowJobs: a no-op on a set already cut at the anchor.
  return { platforms: backfillOf(jobs, now, names), eta: backfillEta(jobs, now, queue) };
};

export const APP_LOADERS: Record<AppWidget, AppLoader> = {
  attention: loadAttention,
  dataQuality: loadDataQuality,
  outcomes: loadOutcomes,
  budgetPacing: loadBudgetPacing,
};
